import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

// KIE Gemini Omni Video pricing (text/image input, no source video):
//   720p/1080p: $0.15 base + $0.075/s
//   4K:         $0.75 base + $0.075/s
// Reproduces 4/6/8/10s = $0.45/$0.60/$0.75/$0.90 (HD) and
// $1.05/$1.20/$1.35/$1.50 (4K).
const BASE_USD_HD = 0.15;
const BASE_USD_4K = 0.75;
const PER_SECOND_USD = 0.075;

function getGeminiOmniCostMicrodollars(
  resolution: "720p" | "1080p" | "4k",
  duration: string,
): number {
  const baseUsd = resolution === "4k" ? BASE_USD_4K : BASE_USD_HD;
  const usd = baseUsd + PER_SECOND_USD * Number(duration);
  return Math.ceil(usd * 1_000_000 * (1 + BRAND_MARKUP));
}

// Gemini Omni accepts up to 7 reference images.
const ATTACHMENT_SLOTS = Array.from({ length: 7 }, (_, index) => ({
  type: "image" as const,
  allowedFormats: ["image/jpeg", "image/png", "image/webp"],
  label: `Image ${index + 1}`,
  slot: `reference_image_${index + 1}`,
  group: "reference",
  required: false,
}));

const geminiOmniVideoParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16"]).default("9:16"),
    resolution: z.enum(["720p", "1080p", "4k"]).default("720p"),
    duration: z.enum(["4", "6", "8", "10"]).default("8"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const geminiOmniVideo = defineModel({
  key: "gemini-omni-video",
  stepType: "video",
  spec: {
    label: "Gemini Omni",
    description:
      "Multimodal video generation from text and up to 7 reference images",
    icon: "gemini-icon.svg",
    featured: true,
    tags: ["video-gen"],
    info: "Generating a video with this model takes a few minutes. Optionally add reference images for image-to-video.",
    features: {
      aspectRatio: true,
      duration: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: ATTACHMENT_SLOTS,
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
        ],
      },
      resolution: {
        items: [
          { label: "720p", value: "720p" },
          { label: "1080p", value: "1080p" },
          { label: "4K", value: "4k" },
        ],
      },
      duration: {
        items: [
          { label: "4s", value: "4" },
          { label: "6s", value: "6" },
          { label: "8s", value: "8" },
          { label: "10s", value: "10" },
        ],
      },
    },
    params: geminiOmniVideoParams,
  },
  providers: [
    {
      provider: "kie",
      modelId: "gemini-omni-video",
      estimateCost(params) {
        return getGeminiOmniCostMicrodollars(
          params.resolution,
          params.duration,
        );
      },
      estimateCostPerSecond(params) {
        return (
          getGeminiOmniCostMicrodollars(params.resolution, params.duration) /
          Number(params.duration)
        );
      },
      costPerSecond: getGeminiOmniCostMicrodollars("720p", "8") / 8,
      toPayload(params) {
        const imageUrls =
          params.attachments
            ?.filter((file) => file.slot.startsWith("reference_image_"))
            .map((file) => file.data ?? file.url)
            .filter((url): url is string => Boolean(url)) ?? [];

        return {
          model: "gemini-omni-video",
          input: {
            prompt: params.prompt,
            duration: params.duration,
            aspect_ratio: params.aspectRatio,
            resolution: params.resolution,
            ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
          },
        };
      },
    },
  ],
});
